import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PageHeader from '../components/PageHeader';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  CheckCircle, 
  Briefcase, 
  GraduationCap, 
  Clock,
  X,
  Upload,
  FileText,
  Users
} from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import Translate from '../components/Translate';
import { useTranslation } from 'react-i18next';
import { getLangText } from '../utils/langHelper';
import Swal from 'sweetalert2';

const Contact = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [mainView, setMainView] = useState('contact'); // 'contact' or 'careers'
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    subject: 'Product Inquiry',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Careers state
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [email, setEmail] = useState('');
  const [experience, setExperience] = useState('');
  const [resumeData, setResumeData] = useState('');
  const [resumeName, setResumeName] = useState('');
  const [submittingApp, setSubmittingApp] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'jobs'), (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setJobs(items);
    });
    return unsub;
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await addDoc(collection(db, "inquiries"), {
        ...formData,
        timestamp: serverTimestamp()
      });
      setSubmitted(true);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        subject: 'Product Inquiry',
        message: ''
      });
    } catch (error) {
      console.error("Error adding document: ", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 800 * 1024) {
      Swal.fire({
        title: 'File Too Large',
        text: 'Please select a resume file smaller than 800 KB.',
        icon: 'error',
        confirmButtonColor: '#2E7D32'
      });
      e.target.value = '';
      return;
    }

    setResumeName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      setResumeData(event.target.result);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Resume uploaded successfully',
        showConfirmButton: false,
        timer: 1500
      });
    };
    reader.readAsDataURL(file);
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !contact.trim() || !email.trim() || !experience.trim() || !resumeData) {
      Swal.fire('Error', 'Please fill in all fields and upload your resume.', 'error');
      return;
    }

    setSubmittingApp(true);
    try {
      await addDoc(collection(db, 'applications'), {
        role: selectedJob.title,
        name: name.trim(),
        contact: contact.trim(),
        email: email.trim(),
        experience: experience.trim(),
        resume: resumeData,
        resumeName,
        status: 'Pending',
        createdAt: serverTimestamp()
      });

      Swal.fire({
        title: 'Application Sent!',
        text: 'Thank you for applying. We will review your application soon.',
        icon: 'success',
        confirmButtonColor: '#2E7D32'
      });

      setSelectedJob(null);
      setName('');
      setContact('');
      setEmail('');
      setExperience('');
      setResumeData('');
      setResumeName('');
    } catch (error) {
      console.error('Error submitting application:', error);
      Swal.fire('Error', 'Could not submit your application. Please try again.', 'error');
    } finally {
      setSubmittingApp(false);
    }
  };

  // Dynamic header titles
  const headerTitle = mainView === 'contact' ? 'Get In Touch' : 'Build Your Future With Us';
  const headerSubtitle = mainView === 'contact'
    ? "Have questions about our products, services, or farmer registration? We're here to help."
    : 'Join a team of 2,000+ professionals committed to innovation, sustainability, and rural growth.';

  return (
    <div className="min-h-screen bg-cream font-body selection:bg-emerald selection:text-white">
      <PageHeader
        title={<Translate>{headerTitle}</Translate>}
        subtitle={<Translate>{headerSubtitle}</Translate>}
      />

      {/* ── Main View Toggle Button ── */}
      <div className="pt-12 flex justify-center">
        <div className="flex bg-white p-1.5 rounded-2xl border border-gray-100 shadow-premium gap-1">
          <button
            onClick={() => setMainView('contact')}
            className={`px-8 py-3 rounded-xl text-xs font-heading font-extrabold uppercase tracking-wider transition-all duration-300 cursor-pointer ${
              mainView === 'contact'
                ? 'bg-emerald text-white shadow-emerald'
                : 'text-slate hover:text-emerald hover:bg-cream'
            }`}
          >
            <Translate>Contact Us</Translate>
          </button>
          <button
            onClick={() => setMainView('careers')}
            className={`px-8 py-3 rounded-xl text-xs font-heading font-extrabold uppercase tracking-wider transition-all duration-300 cursor-pointer ${
              mainView === 'careers'
                ? 'bg-emerald text-white shadow-emerald'
                : 'text-slate hover:text-emerald hover:bg-cream'
            }`}
          >
            <Translate>Careers</Translate>
          </button>
        </div>
      </div>

      <motion.div
        key={mainView}
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        {mainView === 'contact' ? (
          <>
            {/* Contact Form & Office Cards Section */}
            <section className="py-20 bg-section-gradient border-t border-gray-100">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

                  {/* Registered Office & Factory Cards (Left Column - Two Rows) */}
                  <div className="space-y-6">
                    {/* Card 1: Registered Office */}
                    <div className="bg-white rounded-2xl border border-gray-100/80 shadow-premium p-8 flex flex-col justify-between group hover:shadow-premium-lg transition-all duration-300">
                      <div>
                        <div className="bg-gold/10 text-gold p-3.5 rounded-xl w-fit mb-6 group-hover:bg-gold group-hover:text-white transition-all duration-300">
                          <MapPin size={24} />
                        </div>
                        <h3 className="text-xl font-heading font-bold text-charcoal mb-1"><Translate>Registered Office</Translate></h3>
                        <h4 className="text-base font-heading font-bold text-emerald mb-3"><Translate>Sangram Bhawan</Translate></h4>
                        <p className="text-slate text-xs leading-relaxed mb-6">
                          <Translate>Plot No. 187/1, Tasgaon-Karad Road, Industrial Estate, Palus - 416310</Translate><br />
                          <Translate>Tal. Palus, Dist. Sangli, Maharashtra, India</Translate>
                        </p>
                      </div>
                      <div className="border-t border-gray-100 pt-4">
                        <h5 className="font-heading font-bold text-charcoal mb-2 flex items-center text-xs">
                          <Phone size={14} className="mr-2 text-emerald" />
                          <Translate>Phone Numbers</Translate>
                        </h5>
                        <div className="space-y-1 text-slate font-body font-medium text-xs">
                          <a href="tel:02346226240" className="hover:text-emerald transition-colors block">(02346) 226240</a>
                          <a href="tel:02346229200" className="hover:text-emerald transition-colors block">(02346) 229200</a>
                          <a href="tel:+919890016548" className="hover:text-emerald transition-colors block">+91 98900 16548</a>
                        </div>
                      </div>
                    </div>

                    {/* Card 2: Factory / Works */}
                    <div className="bg-white rounded-2xl border border-gray-100/80 shadow-premium p-8 flex flex-col justify-between group hover:shadow-premium-lg transition-all duration-300">
                      <div>
                        <div className="bg-emerald/10 text-emerald p-3.5 rounded-xl w-fit mb-6 group-hover:bg-emerald group-hover:text-white transition-all duration-300">
                          <MapPin size={24} />
                        </div>
                        <h3 className="text-xl font-heading font-bold text-charcoal mb-1"><Translate>Factory / Works</Translate></h3>
                        <h4 className="text-base font-heading font-bold text-emerald mb-3"><Translate>Sangram Cane Agro Limited</Translate></h4>
                        <p className="text-slate text-xs leading-relaxed mb-6">
                          <Translate>Bhilwadi-Sukhwadi Road, Bhilwadi - 416303</Translate><br />
                          <Translate>Tal. Palus, Dist. Sangli, Maharashtra, India</Translate>
                        </p>
                      </div>
                      <div className="border-t border-gray-100 pt-4">
                        <h5 className="font-heading font-bold text-charcoal mb-2 flex items-center text-xs">
                          <Mail size={14} className="mr-2 text-emerald" />
                          <Translate>Email Support</Translate>
                        </h5>
                        <a href="mailto:sangramcaneagroltd@gmail.com" className="text-slate font-body font-medium text-xs hover:text-emerald transition-colors break-all block">
                          sangramcaneagroltd@gmail.com
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Contact Form */}
                  <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-premium p-10 md:p-14">
                    {submitted ? (
                      <div className="text-center py-12">
                        <div className="bg-emerald/10 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                          <CheckCircle className="text-emerald" size={40} />
                        </div>
                        <h2 className="text-3xl font-heading font-bold mb-4 text-charcoal"><Translate>Message Sent!</Translate></h2>
                        <p className="text-slate text-lg mb-8"><Translate>Thank you for reaching out. We will get back to you shortly.</Translate></p>
                        <button
                          onClick={() => setSubmitted(false)}
                          className="btn-premium px-8 py-3 cursor-pointer"
                        >
                          <Translate>Send Another Message</Translate>
                        </button>
                      </div>
                    ) : (
                      <>
                        <h2 className="text-2xl font-heading font-bold mb-2 text-charcoal"><Translate>Send Us a Message</Translate></h2>
                        <div className="gold-divider mb-8"></div>
                        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Full Name</Translate></label>
                            <input
                              type="text"
                              name="fullName"
                              value={formData.fullName}
                              onChange={handleChange}
                              required
                              placeholder="Enter your name"
                              className="w-full bg-cream border border-gray-200 rounded-xl px-4 py-4 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 transition-all text-sm"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Email Address</Translate></label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              placeholder="Enter your email"
                              className="w-full bg-cream border border-gray-200 rounded-xl px-4 py-4 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 transition-all text-sm"
                            />
                          </div>
                          <div className="space-y-2 md:col-span-2">
                            <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Contact Number</Translate></label>
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              required
                              placeholder="Enter your contact"
                              className="w-full bg-cream border border-gray-200 rounded-xl px-4 py-4 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 transition-all text-sm"
                            />
                          </div>
                          <div className="space-y-2 md:col-span-2">
                            <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Subject</Translate></label>
                            <select
                              name="subject"
                              value={formData.subject}
                              onChange={handleChange}
                              className="w-full bg-cream border border-gray-200 rounded-xl px-4 py-4 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 transition-all text-sm"
                            >
                              <option value="Product Inquiry">Product Inquiry</option>
                              <option value="Farmer Registration">Farmer Registration</option>
                              <option value="Export Quotation">Export Quotation</option>
                              <option value="Career Inquiry">Career Inquiry</option>
                              <option value="Others">Others</option>
                            </select>
                          </div>
                          <div className="space-y-2 md:col-span-2">
                            <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Message</Translate></label>
                            <textarea
                              name="message"
                              value={formData.message}
                              onChange={handleChange}
                              required
                              rows="5"
                              placeholder="Enter your message"
                              className="w-full bg-cream border border-gray-200 rounded-xl px-4 py-4 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 transition-all text-sm resize-none"
                            ></textarea>
                          </div>
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="btn-premium md:col-span-2 py-5 text-sm flex items-center justify-center space-x-2 cursor-pointer"
                          >
                            {isSubmitting ? (
                              <span><Translate>Sending...</Translate></span>
                            ) : (
                              <>
                                <Send size={16} />
                                <span><Translate>Send Message</Translate></span>
                              </>
                            )}
                          </button>
                        </form>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Map Section */}
            <section className="h-[500px] w-full relative overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d232143.78990833118!2d74.3159371!3d16.9714856!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc113e6309bc29b%3A0xd594020dd22550cb!2sSangram%20Cane%20Agro%20Sugar%20Factory%2C%20Bhilawadi!5e1!3m2!1sen!2sin!4v1782456268419!5m2!1sen!2sin"
                className="w-full h-full border-0"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                title="Sangram Cane Agro Sugar Factory Location Map"
              ></iframe>
            </section>
          </>
        ) : (
          <>
            {/* Open Positions */}
            <section className="py-20 bg-white border-t border-gray-100">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-16">
                  <span className="section-label block mb-4"><Translate>Opportunities</Translate></span>
                  <h2 className="section-title mb-2"><Translate>Current Openings</Translate></h2>
                  <p className="text-slate text-lg"><Translate>Find a role that matches your passion and skills.</Translate></p>
                </div>

                <div className="space-y-6">
                  {jobs.map((job, idx) => {
                    const jobTitle = getLangText(job, 'title', lang);
                    const jobDept = getLangText(job, 'dept', lang);
                    const jobType = getLangText(job, 'type', lang);
                    const jobLocation = getLangText(job, 'location', lang);
                    const jobSkills = getLangText(job, 'skills', lang);
                    const jobQual = getLangText(job, 'qualification', lang);

                    return (
                      <div 
                        key={job.id || idx}
                        className="bg-cream rounded-3xl border border-gray-150 shadow-premium p-8 sm:p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6 group hover:shadow-premium-lg transition-all duration-300"
                      >
                        <div className="space-y-5 max-w-[85%]">
                          <div>
                            <h3 className="text-2xl sm:text-3xl font-heading font-extrabold mb-2 text-charcoal group-hover:text-emerald transition-colors leading-snug">
                              <Translate>{jobTitle}</Translate>
                            </h3>
                            <div className="flex flex-wrap gap-6 text-base sm:text-lg text-slate font-body font-bold mt-3">
                              <span className="flex items-center">
                                <Briefcase size={20} className="mr-2 text-emerald" /> 
                                <Translate>{jobDept}</Translate>
                              </span>
                              <span className="flex items-center">
                                <Clock size={20} className="mr-2 text-gold" /> 
                                <Translate>{jobType}</Translate>
                              </span>
                              <span className="flex items-center">
                                <MapPin size={20} className="mr-2 text-sage-dark" /> 
                                <Translate>{jobLocation}</Translate>
                              </span>
                              <span className="flex items-center bg-emerald/10 text-emerald text-sm px-3 py-1 rounded-full font-bold">
                                <Users size={18} className="mr-1.5" />
                                <Translate>Vacancies</Translate>: {job.vacancies || '1'}
                              </span>
                            </div>
                          </div>

                          {/* Skills Required */}
                          {jobSkills && (
                            <div className="space-y-2.5">
                              <span className="text-sm font-heading font-extrabold text-slate/70 uppercase tracking-wider block">
                                <Translate>Skills Required</Translate>
                              </span>
                              <div className="flex flex-wrap gap-2.5">
                                {jobSkills.split(',').map((skill, sIdx) => (
                                  <span key={sIdx} className="bg-emerald/10 text-emerald text-sm px-4 py-2 rounded-xl font-extrabold border border-emerald/10">
                                    <Translate>{skill.trim()}</Translate>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Qualification */}
                          {jobQual && (
                            <div className="flex items-center gap-2.5 text-base sm:text-lg text-slate pt-1">
                              <GraduationCap size={22} className="text-emerald flex-shrink-0" />
                              <span className="font-semibold">
                                <Translate>Qualification</Translate>: <span className="text-charcoal font-bold text-lg"><Translate>{jobQual}</Translate></span>
                              </span>
                            </div>
                          )}
                        </div>
                        <button 
                          onClick={() => setSelectedJob({ ...job, title: jobTitle })}
                          className="btn-premium whitespace-nowrap text-lg py-4 px-9 cursor-pointer"
                        >
                          <Translate>Apply Now</Translate>
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-16 text-center">
                  <p className="text-slate text-lg mb-6 font-body font-medium"><Translate>Don't see a matching role?</Translate></p>
                  <button 
                    onClick={() => setSelectedJob({ title: 'General Application' })}
                    className="text-gold font-heading font-bold text-lg hover:underline decoration-2 underline-offset-8 cursor-pointer"
                  >
                    <Translate>Send us your Resume anyway</Translate>
                  </button>
                </div>
              </div>
            </section>
          </>
        )}
      </motion.div>

      {/* Application Popup Modal */}
      <AnimatePresence>
        {selectedJob && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/60 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-3xl border border-gray-100 shadow-premium-xl max-w-lg w-full overflow-hidden relative"
            >
              {/* Modal Header */}
              <div className="bg-forest p-6 text-white relative">
                <div className="absolute inset-0 pattern-overlay opacity-30 pointer-events-none"></div>
                <button 
                  type="button"
                  onClick={() => setSelectedJob(null)}
                  className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/30 rounded-full text-white transition-all cursor-pointer z-30 flex items-center justify-center"
                  aria-label="Close form"
                >
                  <X size={20} />
                </button>
                <div className="relative z-10 space-y-1">
                  <span className="text-[10px] font-heading font-bold text-gold-light uppercase tracking-wider block">
                    <Translate>Job Application Form</Translate>
                  </span>
                  <h3 className="text-xl font-heading font-extrabold">
                    {selectedJob.title}
                  </h3>
                </div>
              </div>

              {/* Modal Form */}
              <form onSubmit={handleApplySubmit} className="p-8 space-y-6">
                <div className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-heading font-bold text-slate uppercase tracking-wider block"><Translate>Full Name</Translate></label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your name"
                      className="w-full bg-cream border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-heading font-bold text-slate uppercase tracking-wider block"><Translate>Contact Number</Translate></label>
                      <input
                        type="tel"
                        value={contact}
                        onChange={(e) => setContact(e.target.value)}
                        placeholder="Enter your contact"
                        className="w-full bg-cream border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-heading font-bold text-slate uppercase tracking-wider block"><Translate>Email Address</Translate></label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="w-full bg-cream border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-heading font-bold text-slate uppercase tracking-wider block"><Translate>Total Experience</Translate></label>
                    <input
                      type="text"
                      value={experience}
                      onChange={(e) => setExperience(e.target.value)}
                      placeholder="e.g. 3 Years in Sugar Production"
                      className="w-full bg-cream border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                      required
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-heading font-bold text-slate uppercase tracking-wider block"><Translate>Resume File (PDF/Word)</Translate></label>
                    <div className="border border-dashed border-gray-250 hover:border-emerald/50 rounded-xl p-4 bg-ivory/50 transition-all duration-300 flex items-center justify-between cursor-pointer relative group">
                      <input
                        type="file"
                        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={handleFileChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        required
                      />
                      <div className="flex items-center gap-3">
                        <Upload size={18} className="text-gray-400 group-hover:text-emerald transition-colors" />
                        <span className="text-xs font-semibold text-slate">
                          {resumeName ? resumeName : <Translate>Select Resume File (Max 800 KB)</Translate>}
                        </span>
                      </div>
                      {resumeName && <FileText size={16} className="text-emerald" />}
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submittingApp}
                  className="w-full btn-premium flex items-center justify-center gap-2 py-4 cursor-pointer"
                >
                  <Send size={16} />
                  <span>{submittingApp ? <Translate>Submitting...</Translate> : <Translate>Submit Application</Translate>}</span>
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Contact;
